import { useState } from 'react';
import { Alert, Anchor, Button, Container, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loginSchema } from '@expeditor/shared';
import { authApi } from '../../shared/api/auth';
import { useAuthStore } from '../../shared/auth/auth.store';
import { useDemoMode } from '../../shared/demo/useDemoMode';
import { homeRouteForRole, isPathAllowedForRole } from '../../shared/auth/home-route';

export function LoginPage() {
  const nav = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? null;
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);

  const demoMode = useDemoMode();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join(', '));
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await authApi.login(parsed.data);
      setTokens(tokens);
      const me = await authApi.me();
      setUser(me);
      // Уважаем сохранённый `from` только если он доступен этой роли,
      // иначе ведём в личный «дом» (иначе менеджер на /cabinet/profile поймает 403).
      const target =
        from && isPathAllowedForRole(from, me.role) ? from : homeRouteForRole(me.role);
      nav(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось войти');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Title order={2} ta="center" mb="md">
        Вход в систему
      </Title>
      {demoMode && (
        <Alert color="orange" mb="md" title="Демо-стенд">
          <Text size="sm">
            Это демонстрационная площадка. Впервые здесь?{' '}
            <Anchor component={Link} to="/demo">
              Начните с порядка работы и регистрации демо-сотрудника
            </Anchor>
            .
          </Text>
        </Alert>
      )}
      <Paper withBorder shadow="sm" p="lg" radius="md">
        <form onSubmit={onSubmit}>
          <Stack>
            <TextInput
              label="Email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
            />
            <PasswordInput
              label="Пароль"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
            />
            {error && (
              <Text c="red" size="sm">
                {error}
              </Text>
            )}
            <Button type="submit" loading={submitting} fullWidth>
              Войти
            </Button>
            <Text size="sm" ta="center">
              <Anchor component={Link} to="/forgot-password">Забыли пароль?</Anchor>
            </Text>
            <Text size="sm" ta="center">
              Нет аккаунта? <Anchor component={Link} to="/register">Зарегистрироваться</Anchor>
            </Text>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
